/**
 * Collapse concurrent calls of an async function into one in-flight promise.
 * A call made while one is running receives the same promise (the legacy `flushOps`,
 * `refreshFolders` and `drainPreviews` guards were hand-rolled versions of this).
 */
export interface SingleFlight<T> {
  (): Promise<T>;
  /** Whether a call is in flight right now. */
  inFlight(): boolean;
  /** The running promise, if any. */
  current(): Promise<T> | null;
}

export function singleFlight<T>(fn: () => Promise<T>): SingleFlight<T> {
  let running: Promise<T> | null = null;
  const wrapped = (() => {
    if (running) return running;
    running = fn().finally(() => {
      running = null;
    });
    return running;
  }) as SingleFlight<T>;
  wrapped.inFlight = () => running !== null;
  wrapped.current = () => running;
  return wrapped;
}

/**
 * Serialise calls: each invocation waits for the previous one to settle before running.
 * Used so rapid `act()` calls apply their local state in call order.
 */
export function serial(): <T>(task: () => Promise<T>) => Promise<T> {
  let chain: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>) => {
    const next = chain.then(task, task);
    chain = next.catch(() => undefined);
    return next;
  };
}

/** Sleep helper for retry schedules. */
export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
